import type { RouteRecordRaw } from 'vue-router'
import type { Component } from 'vue'
import { MenuInfo } from '@/types/menu'
import { LanguageManager } from './language'

const Layout = () => import('@/layout/Layout.vue')



/**
 * 动态导入组件
 */
function loadComponent(componentPath: string): () => Promise<Component> {
  if (componentPath === 'Layout') return Layout

  // 使用 import.meta.glob 预加载所有组件
  const modules = import.meta.glob('../pages/**/*.vue')

  // 构造完整的组件路径
  const fullPath = `/src/pages${componentPath}.vue`

  // console.log('Trying to load component from:', fullPath)

  // console.log('Loading component path:', componentPath)
  // 尝试不同的路径格式
  const possiblePaths = [
    fullPath,
    `../pages${componentPath}.vue`,
    `/src/pages${componentPath}.vue`,
    componentPath
  ]

  for (const path of possiblePaths) {
    // console.log('Checking path:', path)

    if (modules[path]) {
      // console.log('Found component at:', path)

      return modules[path] as () => Promise<Component>
    }
  }

  return Layout // 临时返回Layout组件
}

/**
 * 生成路由名称
 */
function generateRouteName(path: string): string {
  return path.split('/').filter(Boolean).pop() || ''
}

/**
 * 处理路由路径
 */
function handleRoutePath(path: string, parentPath: string = ''): string {
  // 确保路径以 / 开头
  let normalizedPath = path.startsWith('/') ? path : `/${path}`

  // 如果是子路由且父路径不为空
  if (parentPath) {
    // 移除父路径
    if (normalizedPath.startsWith(parentPath)) {
      normalizedPath = normalizedPath.slice(parentPath.length)
    }
    // 确保子路由路径不以 / 开头
    normalizedPath = normalizedPath.replace(/^\/+/, '')
  }

  return normalizedPath
}

/**
 * 递归生成路由配置
 */
function generateRouteRecords(menus: MenuInfo[], parentPath: string = ''): RouteRecordRaw[] {
  const currentLanguage = LanguageManager.getCurrentLanguage()

  return menus.map(menu => {
    // 处理路由路径
    const path = handleRoutePath(menu.routePath, parentPath)

    // 处理 componentPath
    let component = null
    if (menu.componentPath) {
      // 过滤掉 "null" 和空格字符串
      const componentPath = menu.componentPath.trim()
      if (componentPath !== "null" && componentPath !== "") {
        component = loadComponent(componentPath)
      }
    }
    let labels = ''
    try {
      const i18nContent = JSON.parse(menu.menuName as string || '{"zh-MO":"","en-US":"","pt-PT":""}')
      switch (currentLanguage) {
        case 'zh_TW':
          labels = i18nContent['zh-MO'] || ''
          break
        case 'en_US':
          labels = i18nContent['en-US'] || ''
          break
        case 'pt_PT':
          labels = i18nContent['pt-PT'] || ''
          break
        default:
          labels = i18nContent['zh-MO'] || ''
      }
    } catch (e) {
      console.warn('Failed to parse i18n title:', e)
      labels = menu.menuName || ''
    }
    const route: RouteRecordRaw = {
      path,
      name: generateRouteName(menu.routePath),
      component,
      meta: {
        title: labels,
        icon: menu.icon,
        hidden: menu.status !== 1,
        permission: menu.permission,
        keepAlive: true,
        breadcrumb: true
      }
    }

    // 处理子路由
    if (menu.children && menu.children.length > 0) {
      const showChildren = menu.children.filter(child => child.type !== 2)
      if (showChildren.length > 0) {
        route.children = generateRouteRecords(showChildren, menu.routePath)
      }
    }

    return route
  })
}

// 递归过滤菜单数据
const filterMenus = (menus: MenuInfo[]): MenuInfo[] => {
  return menus.map(menu => ({
    ...menu,
    children: menu.children
      ? menu.children.filter(child => child.type === 1 || child.type === 2)
      : []
  })).filter(menu => menu.type === 1)
}

/**
 * 将菜单数据转换为路由配置
 */
export function generateRoutesFromMenu(menuData: any): RouteRecordRaw[] {
  if (!menuData || !Array.isArray(menuData)) {
    return []
  }

  return generateRouteRecords(menuData)
} 